<script setup>

    import { reactive, ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
    import { Modal } from "bootstrap";

    import { triggerUrlDownload, unfocus, scrollTo } from "@/services/utils.js";
    import { useAuthStore } from '@/stores/auth.store.js';
    import useAlertStore from '@/stores/alert.store.js';
    import useFileManagerStore from '@/stores/filemanager.store.js';
    import axios from "axios";
    import {storeToRefs} from "pinia";
    import JSZip from "jszip";
    import { saveAs } from "file-saver";

    // Bytes thresholds for the download confirmation banner.
    const DOWNLOAD_WARN_BYTES = 500 * 1024 * 1024; // 500 MB → soft warn
    const DOWNLOAD_HIGH_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB → loud warn
    const DOWNLOAD_BLOCK_BYTES = 4 * 1024 * 1024 * 1024; // 4 GB → block (browser memory ceiling)
    const DOWNLOAD_FETCH_CONCURRENCY = 4;

    const UPLOAD_CONCURRENCY = 3;
    const SPEED_WINDOW_MS = 4000;
    const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100 MB
    const MIN_PART_SIZE = 8 * 1024 * 1024; // 8 MB
    const MAX_PARTS = 10000; // S3 hard cap
    const PART_CONCURRENCY = 4;

    const authStore = useAuthStore();

    const {user} = storeToRefs(authStore);

    const alertStore = useAlertStore();
    const managerStore = useFileManagerStore();

    const MAX_ZIP_DOWNLOAD_SIZE = 2*1024*1024*1024; // 2Gb

    const aws = authStore.aws;

    const currentPath = managerStore.currentPath;

    const props = defineProps(['nodes']);
    const emit = defineEmits(['refresh', 'createFolder']);


    const newFolderModalRef = ref(null);
    let newFolderModal;
    const newFolderModalForm = reactive({name: '', valid_name:false, status: null});

    const deleteFilesModalRef = ref(null);
    let deleteFilesModal;
    const deleteFilesModalForm = reactive({files:[], status:null, hasDirs:false, dirCount:0, confirmStep:0, totalBytes:0})

    const downloadFilesModalRef = ref(null);
    let downloadFilesModal;
    // status: null | 'preparing' | 'confirm' | 'downloading' | 'zipping' | 'done' | 'error' | 'cancelled'
    // files: [{ key, relPath, size, loaded, status }]
    const downloadFilesModalForm = reactive({
        files: [],
        status: null,
        zipName: 'download.zip',
        totalBytes: 0,
        totalCount: 0,
        loadedBytes: 0,
        completedCount: 0,
        zipPercent: 0,
        warningLevel: null, // null | 'medium' | 'high' | 'block'
        controller: null,
    });

    const uploadFilesModalRef = ref(null);
    let uploadFilesModal;
    const uploadFilesDialogRef = ref(null);
    // status: null | 'preparing' | 'active' | 'done' | 'aborted' | 'error'
    // file: { name, key, size, type, obj, status, progress, loaded, error, controller }
    //   file.status: 'pending' | 'upload' | 'done' | 'error' | 'cancelled'
    const uploadFilesModalForm = reactive({files:[], status:null, startedAt:0, finishedAt:0, speed:0, _samples:[]});
    let speedTickHandle = null;

    onMounted(() => {
        if (newFolderModalRef.value) {
            newFolderModal = new Modal(newFolderModalRef.value);
        }
        if (deleteFilesModalRef.value) {
            deleteFilesModal = new Modal(deleteFilesModalRef.value);
        }
        if (downloadFilesModalRef.value) {
            downloadFilesModal = new Modal(downloadFilesModalRef.value);
        }
        if (uploadFilesModalRef.value) {
            uploadFilesModal = new Modal(uploadFilesModalRef.value);
        }
    })

    const onNewFolderInputChange = (event) => {
        const FOLDER_NAME_REGEX = new RegExp('^[^\\\\\/\?\%\*\:\|\"<>]+$');
        const MAX_FOLDER_NAME_LENGTH = 100;
        const name = event.target.value;

        newFolderModalForm.valid_name = (name.length <= MAX_FOLDER_NAME_LENGTH) && FOLDER_NAME_REGEX.test(name);
    }

    const showNewFolderModal = () => {
        newFolderModalForm.name = '';
        newFolderModalForm.valid_name = false;
        newFolderModalForm.status = null;
        newFolderModal.show();
    }

    const refresh = () => {
        emit('refresh');
    }

    const createFolder = async () => {
      try {
        newFolderModalForm.status = 'loading';

        let key = ''
        if(currentPath.key) {
          key = currentPath.key + `${newFolderModalForm.name}/`;
        }else{
          key = `${newFolderModalForm.name}/`;
        }



        const response = await axios.post(import.meta.env.VITE_API_BACKEND_URL+'/api/folder', {
          key,
          withCredentials: true
        });

        if (response.status !== 200) {
          throw new Error("Create failed");
        }

        emit('createFolder', { name: newFolderModalForm.name, key });

        newFolderModalForm.status = null;

      } catch (e) {
        newFolderModalForm.status = 'error';
        console.error(e);
        alertStore.error("Unable to create folder.");
      }

      newFolderModal.hide();
    };


    const showDeleteFilesModal = async () => {
      deleteFilesModalForm.files = [];
      deleteFilesModalForm.totalBytes = 0;
      deleteFilesModalForm.status = 'loading';
      deleteFilesModalForm.hasDirs = props.nodes.some(n => n.data.filetype === '/dir');
      deleteFilesModalForm.dirCount = props.nodes.filter(n => n.data.filetype === '/dir').length;
      deleteFilesModalForm.confirmStep = 0;
      deleteFilesModal.show();
      try {
        const response = await axios.post(import.meta.env.VITE_API_BACKEND_URL+'/api/files/prepare-delete', {
          nodes: props.nodes.map(node => ({
            key: node.data.key,
            is_dir: node.data.filetype === '/dir',
            size: node.data.size ?? 0,
          }))
        });

        deleteFilesModalForm.files = response.data.files;
        deleteFilesModalForm.totalBytes = response.data.totalBytes ?? 0;
        deleteFilesModalForm.status = 'ready';

      } catch (err) {
        // The API surfaces its own error message for known cases
        // (e.g. a folder containing subfolders); fall back to a
        // generic message otherwise.
        const apiMessage = err?.response?.data?.error;
        alertStore.error(apiMessage || "Could not obtain a list of files for deletion.");
        console.error(err);
        deleteFilesModal.hide();
      }
    };

    // Files get one click to delete; folders get two — the first click
    // moves to a stronger "are you sure" prompt, the second triggers
    // the actual delete. The second step also surfaces the expanded
    // file count from prepare-delete so the user sees the blast radius.
    const onDeleteConfirmClick = () => {
        if (deleteFilesModalForm.hasDirs && deleteFilesModalForm.confirmStep === 0) {
            deleteFilesModalForm.confirmStep = 1;
            return;
        }
        deleteFiles();
    };


    const showUploadFilesDialog = () => {
        uploadFilesDialogRef.value.value = '';
        uploadFilesDialogRef.value.click();
    }
    const hideUploadFilesModal = () => {
        uploadFilesModal.hide()
    }

    const formatBytes = (n) => {
        if (n == null || isNaN(n)) return '–';
        if (n < 1024) return `${n} B`;
        const units = ['KB','MB','GB','TB'];
        let v = n / 1024;
        for (let i=0;i<units.length;i++) {
            if (v < 1024 || i === units.length - 1) {
                return `${v < 10 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
            }
            v /= 1024;
        }
        return `${n} B`;
    }

    const formatDuration = (seconds) => {
        if (!isFinite(seconds) || seconds < 0) return '–';
        seconds = Math.round(seconds);
        if (seconds < 60) return `${seconds}s`;
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        if (m < 60) return `${m}m ${s}s`;
        const h = Math.floor(m / 60);
        return `${h}h ${m % 60}m`;
    }

    const uploadStats = computed(() => {
        const files = uploadFilesModalForm.files;
        const total = files.reduce((a,f) => a + (f.size || 0), 0);
        const loaded = files.reduce((a,f) => a + (f.loaded || 0), 0);
        const doneCount = files.filter(f => f.status === 'done').length;
        const errorCount = files.filter(f => f.status === 'error').length;
        const cancelledCount = files.filter(f => f.status === 'cancelled').length;
        const activeCount = files.filter(f => f.status === 'upload').length;
        const percent = total > 0 ? Math.min(100, (loaded / total) * 100) : 0;
        const speed = uploadFilesModalForm.speed || 0;
        const remaining = Math.max(0, total - loaded);
        const eta = speed > 0 ? remaining / speed : Infinity;
        return {
            total, loaded, percent, speed, eta,
            doneCount, errorCount, cancelledCount, activeCount,
            fileCount: files.length,
        };
    });

    const selectUploadFiles = (event) => {
        const files = event?.target?.files;
        if (!files || !files.length) { return; }
        for (let file of files) { file.filepath = file.name; }
        uploadFiles(files);
    }

    const startSpeedTicker = () => {
        if (speedTickHandle) return;
        speedTickHandle = setInterval(() => {
            const now = Date.now();
            const loaded = uploadFilesModalForm.files.reduce((a,f) => a + (f.loaded || 0), 0);
            uploadFilesModalForm._samples.push({ t: now, loaded });
            // Keep only samples within the rolling window
            while (uploadFilesModalForm._samples.length > 1
                   && now - uploadFilesModalForm._samples[0].t > SPEED_WINDOW_MS) {
                uploadFilesModalForm._samples.shift();
            }
            const samples = uploadFilesModalForm._samples;
            if (samples.length >= 2) {
                const first = samples[0];
                const last = samples[samples.length - 1];
                const dt = (last.t - first.t) / 1000;
                uploadFilesModalForm.speed = dt > 0 ? Math.max(0, (last.loaded - first.loaded) / dt) : 0;
            } else {
                uploadFilesModalForm.speed = 0;
            }
        }, 500);
    }

    const stopSpeedTicker = () => {
        if (speedTickHandle) {
            clearInterval(speedTickHandle);
            speedTickHandle = null;
        }
        uploadFilesModalForm.speed = 0;
        uploadFilesModalForm._samples = [];
    }

    const uploadFiles = async (fileList) => {
        // Reset modal state
        uploadFilesModalForm.files = Array.from(fileList).map((obj) => reactive({
            obj,
            name: obj.name,
            path: obj.filepath || obj.name,
            key: null,
            type: obj.type || 'application/octet-stream',
            size: obj.size,
            loaded: 0,
            progress: 0,
            status: 'pending',
            error: null,
            controller: null,
            multipart: obj.size >= MULTIPART_THRESHOLD,
            uploadId: null,
            partProgress: [], // per-part loaded bytes for accurate progress
        }));
        uploadFilesModalForm.status = 'preparing';
        uploadFilesModalForm.startedAt = Date.now();
        uploadFilesModalForm.finishedAt = 0;
        uploadFilesModalForm.speed = 0;
        uploadFilesModalForm._samples = [];
        uploadFilesModal.show();

        // Single-PUT files share one bulk presign request.
        const singleFiles = uploadFilesModalForm.files.filter(f => !f.multipart);
        if (singleFiles.length > 0) {
            try {
                const response = await axios.post(import.meta.env.VITE_API_BACKEND_URL+'/api/upload/presign', {
                    currentPath: currentPath.key ?? '',
                    files: singleFiles.map(f => ({ name: f.name, type: f.type })),
                }, { withCredentials: true });
                const presignByName = new Map(response.data.files.map(p => [p.name, p]));
                for (const f of singleFiles) {
                    const p = presignByName.get(f.name);
                    if (p) { f.key = p.key; f._url = p.url; f._contentType = p.contentType; f.logId = p.logId; }
                }
            } catch (e) {
                console.error(e);
                uploadFilesModalForm.status = 'error';
                for (const f of singleFiles) {
                    if (f.status === 'pending') { f.status = 'error'; f.error = 'Could not authorize upload'; }
                }
                alertStore.error(e?.response?.data?.error || 'Unable to start upload.');
                return;
            }
        }

        uploadFilesModalForm.status = 'active';
        startSpeedTicker();

        // Concurrency-limited runner across files.
        const queue = [...uploadFilesModalForm.files];
        const runWorker = async () => {
            while (queue.length) {
                const f = queue.shift();
                if (!f || f.status !== 'pending') continue;
                await runOneUpload(f);
            }
        };
        const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, runWorker);
        await Promise.all(workers);

        stopSpeedTicker();
        uploadFilesModalForm.finishedAt = Date.now();

        const errors = uploadFilesModalForm.files.filter(f => f.status === 'error').length;
        const aborted = uploadFilesModalForm.status === 'aborted';
        if (errors === 0 && !aborted) {
            uploadFilesModalForm.status = 'done';
        } else if (errors > 0 && !aborted) {
            uploadFilesModalForm.status = 'error';
        }

        refresh();
    };

    const runOneUpload = async (f) => {
        if (f.multipart) {
            await runMultipartUpload(f);
        } else {
            await runSinglePutUpload(f);
        }
    }

    // Tell the API how a single-PUT upload ended so it lands in the
    // upload log (the API verifies the object itself). Best-effort: a
    // logging failure never changes the upload's own status.
    const reportUpload = async (f, ok) => {
        if (typeof f.logId !== 'number') return;
        try {
            await axios.post(
                import.meta.env.VITE_API_BACKEND_URL + '/api/upload/confirm',
                { logId: f.logId, key: f.key, ok },
                { withCredentials: true }
            );
        } catch (err) {
            console.error('upload log confirm failed:', f.name, err);
        }
    }

    const runSinglePutUpload = async (f) => {
        if (!f._url) {
            f.status = 'error';
            f.error = 'Missing upload URL';
            return;
        }
        f.status = 'upload';
        f.controller = new AbortController();
        try {
            const result = await axios.put(f._url, f.obj, {
                signal: f.controller.signal,
                headers: { 'Content-Type': f._contentType || 'application/octet-stream' },
                withCredentials: false,
                onUploadProgress: (e) => {
                    f.loaded = e.loaded || 0;
                    f.progress = f.size > 0 ? Math.min(100, (f.loaded / f.size) * 100) : 0;
                },
                timeout: 0,
                transformRequest: [(data) => data],
            });
            if (result.status >= 200 && result.status < 300) {
                f.status = 'done';
                f.loaded = f.size;
                f.progress = 100;
            } else {
                f.status = 'error';
                f.error = `S3 returned ${result.status}`;
            }
        } catch (err) {
            if (axios.isCancel(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
                f.status = 'cancelled';
            } else {
                f.status = 'error';
                f.error = err?.message || 'Upload failed';
                console.error('upload failed:', f.name, err);
            }
        } finally {
            f.controller = null;
        }
        await reportUpload(f, f.status === 'done');
    }

    // Choose a part size large enough to keep us under S3's 10000-part
    // cap, but never below the 5MB minimum (we use 8MB as a comfy floor).
    // The last part is allowed to be smaller than MIN_PART_SIZE.
    const choosePartSize = (size) => {
        const byCap = Math.ceil(size / (MAX_PARTS - 100));
        return Math.max(MIN_PART_SIZE, byCap);
    };

    const runMultipartUpload = async (f) => {
        f.status = 'upload';
        f.controller = new AbortController();
        const signal = f.controller.signal;
        const partSize = choosePartSize(f.size);
        const numParts = Math.max(1, Math.ceil(f.size / partSize));
        f.partProgress = new Array(numParts).fill(0);

        const updateLoaded = () => {
            const sum = f.partProgress.reduce((a,b) => a + b, 0);
            f.loaded = sum;
            f.progress = f.size > 0 ? Math.min(100, (sum / f.size) * 100) : 0;
        };

        let started = false;
        try {
            // 1. Start
            const startRes = await axios.post(
                import.meta.env.VITE_API_BACKEND_URL + '/api/upload/multipart/start',
                { name: f.name, currentPath: currentPath.key ?? '', type: f.type },
                { withCredentials: true, signal }
            );
            f.key = startRes.data.key;
            f.uploadId = startRes.data.uploadId;
            f._contentType = startRes.data.contentType;
            f.logId = startRes.data.logId;
            started = true;

            // 2. Upload parts with bounded concurrency
            const completedParts = new Array(numParts);
            const partQueue = Array.from({ length: numParts }, (_, i) => i);

            const uploadPart = async (idx) => {
                const partNumber = idx + 1;
                const offset = idx * partSize;
                const end = Math.min(f.size, offset + partSize);
                const blob = f.obj.slice(offset, end);

                // Sign this part
                const signRes = await axios.post(
                    import.meta.env.VITE_API_BACKEND_URL + '/api/upload/multipart/sign-part',
                    { key: f.key, uploadId: f.uploadId, partNumber },
                    { withCredentials: true, signal }
                );

                // PUT the part directly to S3
                const putRes = await axios.put(signRes.data.url, blob, {
                    signal,
                    withCredentials: false,
                    timeout: 0,
                    transformRequest: [(data) => data],
                    onUploadProgress: (e) => {
                        f.partProgress[idx] = e.loaded || 0;
                        updateLoaded();
                    },
                });

                const etag = putRes.headers?.etag || putRes.headers?.ETag;
                if (!etag) throw new Error('Missing ETag on part response (CORS ExposeHeaders ETag?)');
                f.partProgress[idx] = blob.size;
                updateLoaded();
                completedParts[idx] = { PartNumber: partNumber, ETag: etag };
            };

            const partWorker = async () => {
                while (partQueue.length) {
                    const idx = partQueue.shift();
                    if (idx === undefined) return;
                    await uploadPart(idx);
                }
            };
            const workers = Array.from({ length: Math.min(PART_CONCURRENCY, numParts) }, partWorker);
            await Promise.all(workers);

            // 3. Complete
            await axios.post(
                import.meta.env.VITE_API_BACKEND_URL + '/api/upload/multipart/complete',
                { key: f.key, uploadId: f.uploadId, parts: completedParts, logId: f.logId },
                { withCredentials: true, signal }
            );

            f.status = 'done';
            f.loaded = f.size;
            f.progress = 100;
        } catch (err) {
            const cancelled = axios.isCancel(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
            if (cancelled) {
                f.status = 'cancelled';
            } else {
                f.status = 'error';
                f.error = err?.response?.data?.error || err?.message || 'Upload failed';
                console.error('multipart upload failed:', f.name, err);
            }
            // Best-effort abort so S3 doesn't keep paying for orphaned parts
            if (started && f.uploadId) {
                try {
                    await axios.post(
                        import.meta.env.VITE_API_BACKEND_URL + '/api/upload/multipart/abort',
                        { key: f.key, uploadId: f.uploadId, logId: f.logId },
                        { withCredentials: true }
                    );
                } catch (abortErr) {
                    console.error('abort multipart failed:', f.name, abortErr);
                }
            }
        } finally {
            f.controller = null;
        }
    }

    const cancelUpload = async (file) => {
        if (file?.status === 'pending') {
            file.status = 'cancelled';
            return;
        }
        if (file?.controller) {
            file.controller.abort();
        }
    }

    const cancelAllUploads = async () => {
        uploadFilesModalForm.status = 'aborted';
        for (const file of uploadFilesModalForm.files) {
            if (file.status === 'pending' || file.status === 'upload') {
                cancelUpload(file);
            }
        }
    }

    onBeforeUnmount(() => { stopSpeedTicker(); });

    // Scroll to last uploading item:
    watch(uploadFilesModalForm, (form) => {
        if (!form?.files) { return; }
        for (let i=form.files.length;i>0;i--) {
            if (form.files[i-1].progress > 0 && form.files[i-1].progress < 100) {
                let elem = document.getElementById(`upload-file-list-item-${i}`);
                if (elem) {
                    scrollTo(elem);
                    break;
                }
            }
        }
    });

    const deleteFiles = async () => {
      try {
        deleteFilesModalForm.progress = 0;
        deleteFilesModalForm.status = 'delete';

        const response = await axios.post(import.meta.env.VITE_API_BACKEND_URL+'/api/files/delete', {
          nodes: props.nodes.map(node => ({
            key: node.data.key,
            is_dir: node.data.filetype === '/dir'
          }))
        });

        if (response.data.success) {
          deleteFilesModalForm.progress = 100;
          refresh();
        } else {
          throw new Error(response.data.message || "Delete failed");
        }

      } catch (e) {
        console.error(e);
        const apiMessage = e?.response?.data?.error;
        alertStore.error(apiMessage || "Unable to delete file.");
      } finally {
        deleteFilesModalForm.status = 'done';
        deleteFilesModal.hide();
      }
    };

    const cancelAllDeletes = () => {
        if (deleteFilesModalForm.status == 'loading') {
            deleteFilesModal.abort_controller.abort();
            deleteFilesModal.hide();
        }

        if (deleteFilesModalForm.status == 'ready') {
            deleteFilesModal.hide();
        } else {
            deleteFilesModalForm.status = 'abort';
        }
    }


    // Fetch a presigned URL for a single key. The API forces
    // Content-Disposition: attachment so opening it in a tab triggers
    // a save (rather than the browser previewing PDFs/images inline).
    const fetchDownloadUrl = async (key, filename) => {
        const response = await axios.get(import.meta.env.VITE_API_BACKEND_URL+'/api/download', {
            params: { key, filename },
            withCredentials: true,
        });
        return response.data.url;
    };

    const triggerDirectDownload = (url) => {
        const a = document.createElement('a');
        a.href = url;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const computeWarningLevel = (totalBytes) => {
        if (totalBytes >= DOWNLOAD_BLOCK_BYTES) return 'block';
        if (totalBytes >= DOWNLOAD_HIGH_BYTES) return 'high';
        if (totalBytes >= DOWNLOAD_WARN_BYTES) return 'medium';
        return null;
    };

    const resetDownloadModal = () => {
        downloadFilesModalForm.files = [];
        downloadFilesModalForm.status = null;
        downloadFilesModalForm.zipName = 'download.zip';
        downloadFilesModalForm.totalBytes = 0;
        downloadFilesModalForm.totalCount = 0;
        downloadFilesModalForm.loadedBytes = 0;
        downloadFilesModalForm.completedCount = 0;
        downloadFilesModalForm.zipPercent = 0;
        downloadFilesModalForm.warningLevel = null;
        downloadFilesModalForm.controller = null;
    };

    // Top-level download dispatcher. A single-file selection still goes
    // straight to the browser via the presigned URL. Anything that
    // needs aggregation (multiple files, any folder) goes through the
    // zip flow with its confirmation modal + progress reporting.
    const downloadFiles = async () => {
        if (props.nodes.length === 0) return;

        const folderNodes = props.nodes.filter(n => n.data.filetype === '/dir');
        const fileNodes = props.nodes.filter(n => n.data.filetype !== '/dir');

        if (folderNodes.length === 0 && fileNodes.length === 1) {
            try {
                const node = fileNodes[0];
                const url = await fetchDownloadUrl(node.data.key, node.data.name);
                triggerDirectDownload(url);
            } catch (err) {
                console.error(err);
                alertStore.error('Unable to download file.');
            }
            return;
        }

        await runZipDownload(folderNodes, fileNodes);
    };

    const runZipDownload = async (folderNodes, fileNodes) => {
        resetDownloadModal();
        downloadFilesModalForm.status = 'preparing';
        downloadFilesModalForm.controller = new AbortController();
        downloadFilesModal.show();

        // Pick a sensible zip name: single-folder selection uses the
        // folder name; everything else falls back to "download.zip".
        if (folderNodes.length === 1 && fileNodes.length === 0) {
            downloadFilesModalForm.zipName = `${folderNodes[0].data.name}.zip`;
        }

        // Build the file list: any explicitly-selected file goes in at
        // the zip root; folders are listed recursively and preserve
        // their internal structure under the folder name.
        const planned = [];
        try {
            for (const node of fileNodes) {
                planned.push({
                    key: node.data.key,
                    relPath: node.data.name,
                    size: node.data.size || 0,
                });
            }
            for (const folder of folderNodes) {
                const res = await axios.get(import.meta.env.VITE_API_BACKEND_URL+'/api/files/list-recursive', {
                    params: { key: folder.data.key },
                    withCredentials: true,
                    signal: downloadFilesModalForm.controller.signal,
                });
                for (const f of res.data.files) {
                    planned.push({
                        key: f.key,
                        relPath: `${folder.data.name}/${f.relPath}`,
                        size: f.size || 0,
                    });
                }
            }
        } catch (err) {
            if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') {
                downloadFilesModalForm.status = 'cancelled';
                return;
            }
            console.error(err);
            downloadFilesModalForm.status = 'error';
            alertStore.error('Could not list files for download.');
            return;
        }

        if (planned.length === 0) {
            downloadFilesModalForm.status = 'error';
            alertStore.error('Nothing to download — the selection is empty.');
            return;
        }

        downloadFilesModalForm.files = planned.map(p => reactive({
            ...p,
            loaded: 0,
            status: 'pending', // 'pending' | 'downloading' | 'done' | 'error'
        }));
        downloadFilesModalForm.totalBytes = planned.reduce((a,p) => a + p.size, 0);
        downloadFilesModalForm.totalCount = planned.length;
        downloadFilesModalForm.warningLevel = computeWarningLevel(downloadFilesModalForm.totalBytes);
        downloadFilesModalForm.status = 'confirm';
    };

    const downloadStats = computed(() => {
        const total = downloadFilesModalForm.totalBytes;
        const loaded = downloadFilesModalForm.loadedBytes;
        const percent = total > 0 ? Math.min(100, (loaded / total) * 100) : 0;
        return { total, loaded, percent };
    });

    const startConfirmedDownload = async () => {
        if (downloadFilesModalForm.warningLevel === 'block') return;

        downloadFilesModalForm.status = 'downloading';
        downloadFilesModalForm.controller = new AbortController();
        const signal = downloadFilesModalForm.controller.signal;

        const zip = new JSZip();
        const queue = [...downloadFilesModalForm.files];

        const downloadOne = async (entry) => {
            entry.status = 'downloading';
            try {
                const url = await fetchDownloadUrl(entry.key, entry.relPath.split('/').pop());
                const res = await axios.get(url, {
                    responseType: 'blob',
                    withCredentials: false,
                    signal,
                    onDownloadProgress: (e) => {
                        const delta = (e.loaded || 0) - entry.loaded;
                        entry.loaded = e.loaded || 0;
                        downloadFilesModalForm.loadedBytes += delta;
                    },
                });
                zip.file(entry.relPath, res.data);
                entry.status = 'done';
                downloadFilesModalForm.completedCount += 1;
            } catch (err) {
                if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') {
                    throw err;
                }
                entry.status = 'error';
                console.error('download failed:', entry.key, err);
                throw err;
            }
        };

        const worker = async () => {
            while (queue.length) {
                if (signal.aborted) return;
                const entry = queue.shift();
                if (!entry) return;
                await downloadOne(entry);
            }
        };

        try {
            const workers = Array.from(
                { length: Math.min(DOWNLOAD_FETCH_CONCURRENCY, queue.length) },
                worker
            );
            await Promise.all(workers);
        } catch (err) {
            if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') {
                downloadFilesModalForm.status = 'cancelled';
            } else {
                downloadFilesModalForm.status = 'error';
                alertStore.error('One or more files failed to download.');
            }
            return;
        }

        // All files in the zip — generate the archive blob.
        downloadFilesModalForm.status = 'zipping';
        try {
            const blob = await zip.generateAsync(
                {
                    type: 'blob',
                    // STORE is fast and avoids redundant work for
                    // already-compressed content (videos, images, PDFs).
                    compression: 'STORE',
                },
                (meta) => { downloadFilesModalForm.zipPercent = meta.percent || 0; }
            );
            saveAs(blob, downloadFilesModalForm.zipName);
            downloadFilesModalForm.status = 'done';
            downloadFilesModal.hide();
        } catch (err) {
            console.error('zip generate failed', err);
            downloadFilesModalForm.status = 'error';
            alertStore.error('Could not build the zip archive.');
        }
    };

    const cancelAllDownloads = () => {
        if (downloadFilesModalForm.controller) {
            downloadFilesModalForm.controller.abort();
        }
        downloadFilesModalForm.status = 'cancelled';
        downloadFilesModal.hide();
    }

    const isRowSelected = () => {
        return (props.nodes.length > 0);
    }

    const isFile = () => {
        if ( !isRowSelected() ) { return false; }
        for (let node of props.nodes) {
            if (node.data.filetype == '/dir') { return false; }
        }
        return true;
    }

    const selectionHasDir = () => {
        for (let node of props.nodes) {
            if (node.data.filetype === '/dir') return true;
        }
        return false;
    }

    const canDeleteSelection = () => {
        if (!isRowSelected()) return false;
        const perms = user.value?.permissions || [];
        if (selectionHasDir()) return perms.includes('delete folders');
        return perms.includes('delete files') || perms.includes('delete folders');
    }

    const canShowDeleteButton = () => {
        const perms = user.value?.permissions || [];
        return perms.includes('delete files') || perms.includes('delete folders');
    }

    const overDownloadCountLimit = () => {
        return (props.nodes.length > 10);
    }

    const isLongDownload = () => {
        return (downloadFilesModalForm?.total_size > 10*1024*1024) ||
                (downloadFilesModalForm?.keys?.length > 50);
    }

    const isActive = () => {
      return authStore.user != null;
    }

    const filesPronoun = (files) => {
        if (files.length == 1) { return files[0].name; }
        else {
			const file_count = files.reduce((total,item) => { return (item.is_dir)?total:total+1 }, 0);
			return `${file_count} file${(file_count>1)?'s':''}`;
		}
    }

    defineExpose({ downloadFiles, uploadFiles, deleteFiles, showDeleteFilesModal });
</script>

<template>
<div id="file-manager-toolbar">
    <!-- Toolbar -->
    <div class="p-1">
        <button type="button" class="btn btn-light mx-1" @focus="unfocus" @click="refresh()" :disabled="!isActive()"><font-awesome-icon icon="fa-solid fa-rotate" /></button>
        <button v-if="user?.permissions?.includes('create folder')" type="button" class="btn btn-light mx-1" @focus="unfocus" @click="showNewFolderModal()" :disabled="!isActive()" ><font-awesome-icon icon="fa-solid fa-folder-plus" /></button>
        <button v-if="user?.permissions?.includes('download files')" type="button" class="btn btn-light mx-1" @focus="unfocus" @click="downloadFiles()" :disabled="!isActive() || !isRowSelected()" ><font-awesome-icon icon="fa-solid fa-download" /></button>
        <button v-if="user?.permissions?.includes('upload files')" type="button" class="btn btn-light mx-1" @focus="unfocus" @click="showUploadFilesDialog()" :disabled="!isActive()" ><font-awesome-icon icon="fa-solid fa-cloud-arrow-up" /></button>
        <button v-if="canShowDeleteButton()" type="button" class="btn btn-light ms-2" @focus="unfocus" @click="showDeleteFilesModal()"
                :disabled="!isActive() || !canDeleteSelection()"
                :title="isRowSelected() && !canDeleteSelection() ? 'You do not have permission to delete folders' : ''"><font-awesome-icon icon="fa-solid fa-trash-can" /></button>
    </div>

    <!-- New folder modal -->
    <div class="modal fade" ref="newFolderModalRef" id="newFolderModal" tabindex="-1" aria-labelledby="newFolderModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="newFolderModalLabel">Create new folder</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cancel"></button>
          </div>
          <div class="modal-body">
            <div class="input-group mb-3" id="new-folder-name-form">
              <span class="input-group-text" id="new-folder-name-addon">Name</span>
              <input type="text" class="form-control" placeholder="Folder name" aria-label="Username" aria-describedby="new-folder-name-addon"
                    :class="{'is-invalid': newFolderModalForm.name && !newFolderModalForm.valid_name }"
                    @input="onNewFolderInputChange" v-model="newFolderModalForm.name">
                <div class="invalid-feedback">Please enter a valid folder name.</div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="(newFolderModalForm.status=='loading')">Cancel</button>
            <button type="button" class="btn btn-purple" :disabled="(!newFolderModalForm.valid_name) || newFolderModalForm.status=='loading'" @click="createFolder">Create</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Files modal -->
    <div class="modal fade" ref="deleteFilesModalRef" id="deleteFilesModal" tabindex="-1" aria-labelledby="deleteFilesModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="deleteFilesModalLabel">Confirm deletion</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cancel"></button>
          </div>
          <div class="modal-body">

            <div v-if="deleteFilesModalForm.status=='loading'" class="row"><div class="col text-center">
                <div class="spinner-border loading-spinner text-center" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
            </div></div>
            <div v-if="deleteFilesModalForm.status=='delete'" class="row"><div class="col text-center">
                <div class="progress">
                  <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" :style="{width:deleteFilesModalForm?.progress +'%'}" :aria-valuenow="deleteFilesModalForm?.progress" aria-valuemin="0" aria-valuemax="100"></div>
                </div>
            </div></div>
            <template v-if="deleteFilesModalForm.status=='ready'">
              <div v-if="!deleteFilesModalForm.hasDirs">
                Delete <strong>{{filesPronoun(deleteFilesModalForm.files)}}</strong>?
              </div>
              <div v-else-if="deleteFilesModalForm.confirmStep === 0">
                Delete <strong>{{ deleteFilesModalForm.dirCount === 1 ? '1 folder' : `${deleteFilesModalForm.dirCount} folders` }}</strong>
                and everything inside
                ({{ deleteFilesModalForm.files.length }} item{{ deleteFilesModalForm.files.length === 1 ? '' : 's' }}<span v-if="deleteFilesModalForm.totalBytes > 0">, {{ formatBytes(deleteFilesModalForm.totalBytes) }}</span>)?
              </div>
              <div v-else class="text-danger">
                <strong>This permanently deletes {{ deleteFilesModalForm.files.length }} item{{ deleteFilesModalForm.files.length === 1 ? '' : 's' }}<span v-if="deleteFilesModalForm.totalBytes > 0"> ({{ formatBytes(deleteFilesModalForm.totalBytes) }})</span> and cannot be undone.</strong><br>
                Click <em>Yes, delete</em> to confirm.
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" :disabled="deleteFilesModalForm.status=='abort'" @click="cancelAllDeletes">Cancel</button>
            <button type="button"
                    :class="deleteFilesModalForm.hasDirs && deleteFilesModalForm.confirmStep === 1 ? 'btn btn-danger' : 'btn btn-purple'"
                    :disabled="deleteFilesModalForm.status!='ready'"
                    @click="onDeleteConfirmClick">
              {{ deleteFilesModalForm.hasDirs && deleteFilesModalForm.confirmStep === 1 ? 'Yes, delete' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Download Files modal -->
    <div class="modal fade" ref="downloadFilesModalRef" id="downloadFilesModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-labelledby="downloadFilesModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="downloadFilesModalLabel">
              <span v-if="downloadFilesModalForm.status === 'preparing'">Preparing download…</span>
              <span v-else-if="downloadFilesModalForm.status === 'confirm'">Download as zip?</span>
              <span v-else-if="downloadFilesModalForm.status === 'downloading'">Downloading {{ downloadFilesModalForm.completedCount }} of {{ downloadFilesModalForm.totalCount }}</span>
              <span v-else-if="downloadFilesModalForm.status === 'zipping'">Building archive…</span>
              <span v-else-if="downloadFilesModalForm.status === 'cancelled'">Download cancelled</span>
              <span v-else-if="downloadFilesModalForm.status === 'error'">Download failed</span>
              <span v-else>Download</span>
            </h5>
          </div>
          <div class="modal-body">

            <!-- Preparing — listing files under the selected folders -->
            <div v-if="downloadFilesModalForm.status === 'preparing'" class="text-center py-3">
              <div class="spinner-border loading-spinner" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <div class="text-muted small mt-2">Listing files…</div>
            </div>

            <!-- Confirm — show totals + warning -->
            <template v-if="downloadFilesModalForm.status === 'confirm'">
              <div class="mb-2">
                You're about to download
                <strong>{{ downloadFilesModalForm.totalCount }} file{{ downloadFilesModalForm.totalCount === 1 ? '' : 's' }}</strong>
                ({{ formatBytes(downloadFilesModalForm.totalBytes) }})
                as <code>{{ downloadFilesModalForm.zipName }}</code>.
              </div>
              <div v-if="downloadFilesModalForm.warningLevel === 'medium'" class="alert alert-warning d-flex align-items-start mb-0">
                <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="me-2 mt-1" />
                <div>
                  This is a sizable download. It may take a while and will use a fair amount of memory while the archive is built in your browser.
                </div>
              </div>
              <div v-else-if="downloadFilesModalForm.warningLevel === 'high'" class="alert alert-danger d-flex align-items-start mb-0">
                <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="me-2 mt-1" />
                <div>
                  <strong>This is a large archive.</strong>
                  Building it can take several minutes and will hold the entire contents in your browser's memory. Close other tabs first, or download these folders in smaller chunks if you can.
                </div>
              </div>
              <div v-else-if="downloadFilesModalForm.warningLevel === 'block'" class="alert alert-danger d-flex align-items-start mb-0">
                <font-awesome-icon icon="fa-solid fa-circle-exclamation" class="me-2 mt-1" />
                <div>
                  <strong>This download is too large for an in-browser zip ({{ formatBytes(downloadFilesModalForm.totalBytes) }}).</strong>
                  Most browsers will run out of memory before it finishes. Please download these folders in smaller pieces.
                </div>
              </div>
            </template>

            <!-- Downloading — overall progress + per-file rows -->
            <template v-if="downloadFilesModalForm.status === 'downloading'">
              <div class="d-flex justify-content-between mb-1">
                <strong>{{ Math.round(downloadStats.percent) }}%</strong>
                <span class="text-muted small">{{ formatBytes(downloadStats.loaded) }} / {{ formatBytes(downloadStats.total) }}</span>
              </div>
              <div class="progress upload-overall-progress mb-3">
                <div class="progress-bar progress-bar-striped progress-bar-animated"
                     role="progressbar"
                     :style="{ width: downloadStats.percent + '%' }"
                     :aria-valuenow="downloadStats.percent" aria-valuemin="0" aria-valuemax="100"></div>
              </div>
              <ul class="list-group" id="download-file-list">
                <li class="list-group-item upload-file-row" v-for="entry in downloadFilesModalForm.files" :key="entry.key">
                  <div class="d-flex align-items-center">
                    <div class="upload-file-icon me-2">
                      <font-awesome-icon class="text-success" v-if="entry.status === 'done'" icon="fa-solid fa-check-circle" />
                      <font-awesome-icon class="text-danger" v-else-if="entry.status === 'error'" icon="fa-solid fa-circle-exclamation" />
                      <font-awesome-icon class="text-primary fa-spin" v-else-if="entry.status === 'downloading'" icon="fa-solid fa-spinner" />
                      <font-awesome-icon class="text-muted" v-else icon="fa-regular fa-circle" />
                    </div>
                    <div class="upload-file-info flex-grow-1">
                      <div class="d-flex justify-content-between">
                        <div class="upload-file-name horizontal-scrollable no-scrollbar">{{ entry.relPath }}</div>
                        <div class="upload-file-meta text-muted small ms-2">
                          <template v-if="entry.status === 'downloading'">{{ formatBytes(entry.loaded) }} / {{ formatBytes(entry.size) }}</template>
                          <template v-else>{{ formatBytes(entry.size) }}</template>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </template>

            <!-- Zipping — generateAsync progress -->
            <template v-if="downloadFilesModalForm.status === 'zipping'">
              <div class="text-center py-2">
                <div class="mb-2">Building <code>{{ downloadFilesModalForm.zipName }}</code>…</div>
                <div class="progress upload-overall-progress">
                  <div class="progress-bar progress-bar-striped progress-bar-animated"
                       role="progressbar"
                       :style="{ width: downloadFilesModalForm.zipPercent + '%' }"
                       :aria-valuenow="downloadFilesModalForm.zipPercent" aria-valuemin="0" aria-valuemax="100"></div>
                </div>
                <div class="text-muted small mt-2">{{ Math.round(downloadFilesModalForm.zipPercent) }}%</div>
              </div>
            </template>

            <div v-if="downloadFilesModalForm.status === 'cancelled'" class="text-muted">Download cancelled.</div>
            <div v-if="downloadFilesModalForm.status === 'error'" class="text-danger">Something went wrong. See console for details.</div>

          </div>
          <div class="modal-footer">
            <template v-if="downloadFilesModalForm.status === 'confirm'">
              <button type="button" class="btn btn-secondary" @click="cancelAllDownloads">Cancel</button>
              <button type="button" class="btn btn-purple"
                      :disabled="downloadFilesModalForm.warningLevel === 'block'"
                      @click="startConfirmedDownload">Download</button>
            </template>
            <template v-else-if="downloadFilesModalForm.status === 'preparing' || downloadFilesModalForm.status === 'downloading' || downloadFilesModalForm.status === 'zipping'">
              <button type="button" class="btn btn-secondary" @click="cancelAllDownloads">Abort</button>
            </template>
            <template v-else>
              <button type="button" class="btn btn-purple" @click="downloadFilesModal.hide()">Close</button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Files modal -->
    <input type="file" ref="uploadFilesDialogRef" multiple @change="selectUploadFiles" style="display:none">
    <div class="modal fade" ref="uploadFilesModalRef" id="uploadFilesModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-labelledby="uploadFilesModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="uploadFilesModalLabel">
              <span v-if="uploadFilesModalForm.status === 'preparing'">Preparing upload…</span>
              <span v-else-if="uploadFilesModalForm.status === 'active'">Uploading {{ uploadStats.doneCount }} of {{ uploadStats.fileCount }}</span>
              <span v-else-if="uploadFilesModalForm.status === 'done'">Upload complete</span>
              <span v-else-if="uploadFilesModalForm.status === 'aborted'">Upload aborted</span>
              <span v-else-if="uploadFilesModalForm.status === 'error'">Upload finished with errors</span>
              <span v-else>Upload files</span>
            </h5>
          </div>
          <div class="modal-body">

            <!-- Overall progress + stats -->
            <div class="upload-summary mb-3">
              <div class="d-flex justify-content-between align-items-baseline mb-1">
                <div class="upload-summary-left">
                  <strong>{{ Math.round(uploadStats.percent) }}%</strong>
                  <span class="text-muted ms-2">{{ formatBytes(uploadStats.loaded) }} / {{ formatBytes(uploadStats.total) }}</span>
                </div>
                <div class="text-muted small upload-summary-right">
                  <span v-if="uploadFilesModalForm.status === 'active' && uploadStats.speed > 0">{{ formatBytes(uploadStats.speed) }}/s</span>
                  <span v-if="uploadFilesModalForm.status === 'active' && isFinite(uploadStats.eta) && uploadStats.eta > 0" class="ms-2">~{{ formatDuration(uploadStats.eta) }} left</span>
                  <span v-else-if="uploadFilesModalForm.status === 'preparing'">authorizing…</span>
                </div>
              </div>
              <div class="progress upload-overall-progress">
                <div class="progress-bar"
                     :class="{
                        'progress-bar-striped progress-bar-animated': uploadFilesModalForm.status === 'preparing' || uploadFilesModalForm.status === 'active',
                        'bg-danger': uploadFilesModalForm.status === 'error',
                        'bg-warning': uploadFilesModalForm.status === 'aborted',
                     }"
                     role="progressbar"
                     :style="{ width: uploadStats.percent + '%' }"
                     :aria-valuenow="uploadStats.percent" aria-valuemin="0" aria-valuemax="100"></div>
              </div>
              <div class="upload-summary-counts text-muted small mt-1">
                <span v-if="uploadStats.doneCount > 0" class="me-3"><font-awesome-icon class="text-success" icon="fa-solid fa-check" /> {{ uploadStats.doneCount }} done</span>
                <span v-if="uploadStats.activeCount > 0" class="me-3"><font-awesome-icon class="text-primary" icon="fa-solid fa-arrow-up-from-bracket" /> {{ uploadStats.activeCount }} uploading</span>
                <span v-if="uploadStats.errorCount > 0" class="me-3 text-danger"><font-awesome-icon icon="fa-solid fa-circle-exclamation" /> {{ uploadStats.errorCount }} failed</span>
                <span v-if="uploadStats.cancelledCount > 0" class="me-3 text-muted"><font-awesome-icon icon="fa-solid fa-xmark" /> {{ uploadStats.cancelledCount }} cancelled</span>
              </div>
            </div>

            <ul class="list-group" id="upload-file-list">
              <li class="list-group-item upload-file-row" v-for="(file,index) in uploadFilesModalForm.files" ref="uploadFilesListRef" :id="'upload-file-list-item-' + (index + 1)" >
                <div class="d-flex align-items-center">
                  <div class="upload-file-icon me-2">
                    <font-awesome-icon class="text-success" v-if="file?.status === 'done'" icon="fa-solid fa-check-circle" />
                    <font-awesome-icon class="text-danger" v-else-if="file?.status === 'error'" icon="fa-solid fa-circle-exclamation" />
                    <font-awesome-icon class="text-muted" v-else-if="file?.status === 'cancelled'" icon="fa-solid fa-circle-xmark" />
                    <font-awesome-icon class="text-primary fa-spin" v-else-if="file?.status === 'upload'" icon="fa-solid fa-spinner" />
                    <font-awesome-icon class="text-muted" v-else icon="fa-regular fa-circle" />
                  </div>
                  <div class="upload-file-info flex-grow-1">
                    <div class="d-flex justify-content-between align-items-baseline">
                      <div class="upload-file-name horizontal-scrollable no-scrollbar">{{ file?.path }}</div>
                      <div class="upload-file-meta text-muted small ms-2">
                        <template v-if="file?.status === 'upload'">
                          {{ formatBytes(file.loaded) }} / {{ formatBytes(file.size) }}
                        </template>
                        <template v-else-if="file?.status === 'done'">
                          {{ formatBytes(file.size) }}
                        </template>
                        <template v-else-if="file?.status === 'error'">
                          <span class="text-danger">{{ file.error || 'Failed' }}</span>
                        </template>
                        <template v-else-if="file?.status === 'cancelled'">
                          Cancelled
                        </template>
                        <template v-else>
                          {{ formatBytes(file.size) }}
                        </template>
                      </div>
                    </div>
                    <div class="progress upload-progress mt-1">
                      <div class="progress-bar"
                           :class="{
                              'bg-success': file?.status === 'done',
                              'bg-danger': file?.status === 'error',
                              'bg-secondary': file?.status === 'cancelled',
                           }"
                           role="progressbar"
                           :style="{ width: (file?.status === 'done' ? 100 : file?.progress || 0) + '%' }"
                           :aria-valuenow="file?.progress || 0" aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                  </div>
                  <div class="upload-status-control ms-2">
                    <button v-if="file?.status === 'pending' || file?.status === 'upload'" type="button" class="btn btn-sm btn-light" @click="cancelUpload(file)" @focus="unfocus" title="Cancel"><font-awesome-icon icon="fa-solid fa-xmark" /></button>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div class="modal-footer">
            <button v-if="uploadFilesModalForm.status === 'active' || uploadFilesModalForm.status === 'preparing'" type="button" class="btn btn-secondary" @click="cancelAllUploads()">Abort all</button>
            <button v-else type="button" class="btn btn-purple" @click="hideUploadFilesModal()">Close</button>
          </div>
        </div>
      </div>
    </div>

</div>
</template>

<style scoped>
    #file-manager-toolbar .btn-light {
        border: 1px solid #ced4da;
        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
    }
    #file-manager-toolbar .btn-light:hover:not(:disabled) {
        border-color: #adb5bd;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }
    #file-manager-toolbar .btn-light:disabled {
        opacity: 0.55;
    }

    #newFolderModal .modal-body {
        min-height:115px;
    }
    #upload-file-list {
        width: 100%;
        max-height: 22em;
        margin: 0 auto;
        overflow-y: auto;
        overflow-x: hidden;
    }
    .upload-summary {
        padding: 0 0.25rem;
    }
    .upload-overall-progress {
        height: 10px;
    }
    .upload-overall-progress .progress-bar {
        background-color: #850f5f;
    }
    .upload-file-row {
        padding: 0.5rem 0.75rem;
    }
    .upload-file-icon {
        width: 20px;
        text-align: center;
        flex-shrink: 0;
    }
    .upload-file-name {
        font-size: 0.95rem;
    }
    .upload-file-meta {
        white-space: nowrap;
        flex-shrink: 0;
    }
    .horizontal-scrollable {
        white-space: nowrap;
        overflow-x: auto;
        overflow-y: hidden;
        margin-left:3px;
    }



    .no-scrollbar {
        scrollbar-color: transparent transparent;
    }
    .no-scrollbar::-webkit-scrollbar {
        height: 0px;
    }
    .no-scrollbar::-webkit-scrollbar-track {
        -webkit-box-shadow: inset 0 0 0 rgba(0, 0, 0, 0);
    }
    .no-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0);
        -webkit-box-shadow: inset 0 0 0 rgba(0, 0, 0, 0);
    }
    .no-scrollbar::-webkit-scrollbar-thumb:window-inactive {
        background: rgba(0, 0, 0, 0);
    }

    .upload-file-info {
        max-width: calc(100% - 30px);
    }
    .upload-progress {
        height: 3px;
    }
    .upload-progress .progress-bar{
        background-color: #850f5f;
    }
    .upload-status-control {
        width: 30px;
        padding: 0;
    }

    #deleteFilesModal .loading-spinner, #downloadFilesModal .loading-spinner {
        width: 3rem;
        height: 3rem;
        color: #850f5f;
    }

    #downloadFilesModal .download-hint {
        font-size: 12px;
        color: gray;
    }
</style>